import { useState } from 'react';

import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaymentsIcon from '@mui/icons-material/Payments';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';

import { invoicePaymentOption } from 'constants-ts/payments';
import Block from '@mui/icons-material/Block';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Undo from '@mui/icons-material/Undo';
import { useCheckPermission } from 'hooks-ts/usePermission';
import { type Invoice, INVOICE_STATUS } from '@a1/shared';

interface ActionsColumnProps {
  invoice: Invoice;
  setSelectedInvoice: React.Dispatch<React.SetStateAction<Invoice | null>>;
  setOpenInvoicePaymentsDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenInvoicePdfDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenSendInvoicePdfDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenMarkAsPaidDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenVoidDialog?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActionsColumn = ({
  invoice,
  setSelectedInvoice,
  setOpenInvoicePaymentsDialog,
  setOpenInvoicePdfDialog,
  setOpenSendInvoicePdfDialog,
  setOpenMarkAsPaidDialog,
  setOpenVoidDialog
}: ActionsColumnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const permissionCheck = useCheckPermission();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <IconButton
        id={`open-action-menu-button-${invoice.id}`}
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="open action menu"
        onClick={handleOpenActionMenu}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`action-menu-${invoice.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseActionMenu}
        slotProps={{
          list: {
            'aria-labelledby': `open-action-menu-button-${invoice.id}`
          }
        }}
      >
        {setOpenInvoicePaymentsDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setSelectedInvoice(invoice);
              setOpenInvoicePaymentsDialog(true);
              handleCloseActionMenu();
            }}
            disabled={!permissionCheck.canViewPaymentTransactions()}
          >
            <ListItemIcon>
              <PaymentsIcon fontSize="small" />
            </ListItemIcon>
            View Invoice Payments
          </MenuItem>
        )}

        {setOpenInvoicePdfDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setSelectedInvoice(invoice);
              setOpenInvoicePdfDialog(true);
              handleCloseActionMenu();
            }}
            disabled={!permissionCheck.canExportSendInvoices()}
          >
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            View Invoice PDF
          </MenuItem>
        )}

        {setOpenSendInvoicePdfDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setSelectedInvoice(invoice);
              setOpenSendInvoicePdfDialog(true);
              handleCloseActionMenu();
            }}
            disabled={!permissionCheck.canExportSendInvoices()}
          >
            <ListItemIcon>
              <AttachEmailIcon fontSize="small" />
            </ListItemIcon>
            Email Invoice
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setSelectedInvoice(invoice);
              setOpenMarkAsPaidDialog?.(true);
              handleCloseActionMenu();
            }}
            disabled={!permissionCheck.canChangeInvoicePaymentStatus()}
          >
            <ListItemIcon>
              <CheckCircle fontSize="small" />
            </ListItemIcon>
            Mark as Paid
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.PAID.id && setOpenInvoicePaymentsDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setSelectedInvoice(invoice);
              setOpenInvoicePaymentsDialog(true);
              handleCloseActionMenu();
            }}
            disabled={!permissionCheck.canChangeInvoicePaymentStatus()}
          >
            <ListItemIcon>
              <Undo fontSize="small" />
            </ListItemIcon>
            Refund
          </MenuItem>
        )}

        {(invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id ||
          (invoice.status === INVOICE_STATUS.PAID.id && invoice.paymentOption !== invoicePaymentOption.ONLINE.id)) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              setSelectedInvoice(invoice);
              setOpenVoidDialog?.(true);
              handleCloseActionMenu();
            }}
            disabled={!permissionCheck.canChangeInvoicePaymentStatus()}
          >
            <ListItemIcon>
              <Block fontSize="small" />
            </ListItemIcon>
            Void
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default ActionsColumn;
